import React, { memo, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import ThemeToggle from './ThemeToggle';
import NavLink, { NAV_ITEMS } from './NavLink';
import ScrollProgress from './ScrollProgress';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface HeaderProps {
  isDark: boolean;
  scrolled: boolean;
  mobileMenuOpen: boolean;
  onToggleMobileMenu: () => void;
  onCloseMobileMenu: () => void;
  topBarVisible?: boolean;
}

/**
 * Header component with navigation, theme toggle, and mobile menu.
 * Includes focus trap for accessibility and scroll progress indicator.
 */
const Header = memo(function Header({
  isDark,
  scrolled,
  mobileMenuOpen,
  onToggleMobileMenu,
  onCloseMobileMenu,
  topBarVisible = false,
}: HeaderProps) {
  const [location] = useLocation();
  const mobileMenuRef = useFocusTrap<HTMLDivElement>(mobileMenuOpen);

  // Listen for escape key to close mobile menu
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        onCloseMobileMenu();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen, onCloseMobileMenu]);

  return (
    <>
      {/* Scroll Progress Indicator */}
      <ScrollProgress color="hsl(var(--primary))" height={3} />
      
      <header
        className={cn(
          'fixed w-full z-40 transition-all duration-300',
          topBarVisible ? 'top-9' : 'top-0',
          scrolled
            ? isDark
              ? 'bg-gray-900 shadow-md py-2'
              : 'bg-white shadow-md py-2'
            : 'bg-transparent py-4'
        )}
        role="banner"  
      >
      <div  className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center" 
          >
            <Link href="/">
              <span className="flex items-center cursor-pointer">
                <img
                  src={
                    isDark || (!scrolled && location === '/')
                      ? '/assets/logo/logo-bk.png'
                      : '/assets/logo/Logo-l.png'
                  }
                  alt="Digital Web Builders"
                  className="h-14 w-auto transition-all duration-300"
                />   
              </span>
            </Link>
          </motion.div>
       
          {/* Desktop Navigation */}
          <motion.nav  
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="hidden md:block bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm px-6 py-2 rounded-full shadow-sm"
          >
            <ul className="flex space-x-6">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.href}
                  href={item.href}
                  label={item.label}
                  variant="desktop"
                  isDark={isDark}
                  scrolled={scrolled}
                />
              ))}
            </ul>
          </motion.nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-3">
            {/* Theme Toggle */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <ThemeToggle />
            </motion.div>

            {/* CTA Button (Desktop) */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="hidden md:block"
            >
              <Link href="/enquiry">
                <span
                  className={cn(
                    'px-4 py-2 rounded-md font-medium transition-all duration-300 cursor-pointer',
                    'hover:shadow-lg hover:brightness-110 inline-flex items-center',
                    isDark
                      ? 'bg-primary text-white border border-primary'
                      : scrolled
                        ? 'bg-primary text-white border border-primary'
                        : 'bg-white text-primary border border-white'
                  )}
                >
                  Get Free Quote
                </span>
              </Link>
            </motion.div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                type="button"
                onClick={onToggleMobileMenu}
                className={cn(
                  'inline-flex items-center justify-center p-2.5 rounded-xl transition-all duration-300',
                  'bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-lg shadow-black/5',
                  'border border-gray-200/50 dark:border-gray-700/50',
                  'text-gray-700 dark:text-gray-200 hover:text-primary hover:border-primary/30'
                )}
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
              >
                <svg
                  className="h-6 w-6"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  {mobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown with focus trap */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={cn(
              'md:hidden shadow-lg overflow-hidden',
              isDark ? 'bg-gray-900' : 'bg-white'
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className="container mx-auto px-4 py-3">
              <nav className="flex flex-col space-y-3" role="navigation">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.href}
                    href={item.href}
                    label={item.label}
                    variant="mobile"
                    isDark={isDark}
                    onClick={onCloseMobileMenu}
                  />
                ))}
                <Link href="/enquiry">
                  <span
                    className="mt-2 px-4 py-2 bg-primary text-white rounded-md text-center font-medium block cursor-pointer border border-primary hover:brightness-110 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    onClick={onCloseMobileMenu}
                  >
                    Get Free Quote
                  </span>
                </Link>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
    </>
  );
});

Header.displayName = 'Header';

export default Header;
